// Hex grid for the MeshMapper (issue #32) and FreqMapper (issue #36) coverage layers. Pure maths, no imports, no map library:
// the grid is written here on purpose (no hex package), and tests/hexCoverage.test.mjs covers it.
//
// One fixed grid: flat-top hexes in Web Mercator metres, about 300 m across (the scale of MeshMapper's own squares). Hexes
// never grow when you zoom out. The server works each layer's data into hexes on this same grid (a hex and its class); the
// browser only paints them. Neighbouring hexes of the same class are drawn as one merged area: no line between them, an
// outline only along the outer edge of the area. The layer is not drawn below zoom 9 (MIN_LAYER_ZOOM).
//
// A hex's size is its width corner to corner, in metres on the ground at the data's own latitude (Mercator stretches
// distances by 1 / cos(latitude), so the grid is scaled to compensate).

export const EARTH_RADIUS_M = 6378137;
const SQRT3 = Math.sqrt(3);

/** Hex width corner to corner, metres on the ground. */
export const HEX_SIZE_M = 300;

// --- the zoom gate -------------------------------------------------------------------------------------------------

/** The layer is drawn from this zoom up; below it nothing is drawn and no hex geometry is built. */
export const MIN_LAYER_ZOOM = 9;

export function layerVisibleAtZoom(zoom: number): boolean {
  return zoom >= MIN_LAYER_ZOOM;
}

// --- projection and the grid ---------------------------------------------------------------------------------------

export function project(lat: number, lon: number): [number, number] {
  const phi = (lat * Math.PI) / 180;
  return [EARTH_RADIUS_M * ((lon * Math.PI) / 180), EARTH_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + phi / 2))];
}

export function unproject(x: number, y: number): [number, number] {
  const lon = ((x / EARTH_RADIUS_M) * 180) / Math.PI;
  const lat = ((2 * Math.atan(Math.exp(y / EARTH_RADIUS_M)) - Math.PI / 2) * 180) / Math.PI;
  return [lat, lon];
}

/** Hex radius (centre to corner) in Mercator metres for a ground width `sizeM` at latitude `refLat`. */
export function hexRadiusMercator(sizeM: number, refLat: number): number {
  return sizeM / 2 / Math.cos((refLat * Math.PI) / 180);
}

/** The hex (axial q, r) that contains a point. */
export function hexAt(lat: number, lon: number, sizeM: number, refLat: number): [number, number] {
  const [x, y] = project(lat, lon);
  const R = hexRadiusMercator(sizeM, refLat);
  const fq = ((2 / 3) * x) / R;
  const fr = ((-1 / 3) * x + (SQRT3 / 3) * y) / R;
  return cubeRound(fq, fr);
}

function cubeRound(fq: number, fr: number): [number, number] {
  const fs = -fq - fr;
  let q = Math.round(fq);
  let r = Math.round(fr);
  const s = Math.round(fs);
  const dq = Math.abs(q - fq);
  const dr = Math.abs(r - fr);
  const ds = Math.abs(s - fs);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return [q + 0, r + 0]; // + 0 turns -0 into 0
}

/** Centre of a hex as [lat, lon]. */
export function hexCentre(q: number, r: number, sizeM: number, refLat: number): [number, number] {
  const R = hexRadiusMercator(sizeM, refLat);
  return unproject(R * 1.5 * q, R * ((SQRT3 / 2) * q + SQRT3 * r));
}

/** Corner k (0 to 5, at k x 60 degrees from the east) of a hex as [lon, lat]. */
export function hexCorner(q: number, r: number, k: number, sizeM: number, refLat: number): [number, number] {
  const R = hexRadiusMercator(sizeM, refLat);
  const a = (Math.PI / 3) * k;
  const [lat, lon] = unproject(R * 1.5 * q + R * Math.cos(a), R * ((SQRT3 / 2) * q + SQRT3 * r) + R * Math.sin(a));
  return [lon, lat];
}

/** The closed ring of a hex as [lon, lat] pairs (7 points, the first repeated), for GeoJSON. */
export function hexRing(q: number, r: number, sizeM: number, refLat: number): [number, number][] {
  const ring: [number, number][] = [];
  for (let k = 0; k < 6; k += 1) ring.push(hexCorner(q, r, k, sizeM, refLat));
  ring.push(ring[0]);
  return ring;
}

/** The neighbour across edge k (the edge from corner k to corner k + 1) as an axial step [dq, dr]. */
export const NEIGHBOUR_STEP: readonly (readonly [number, number])[] = [
  [1, 0], [0, 1], [-1, 1], [-1, 0], [0, -1], [1, -1],
];

// --- cells ---------------------------------------------------------------------------------------------------------

/** What the outline and the polygons need from any layer's hexes: where the hex is and which class it is. The server sends
 * each layer as rows of hex and class; merging, the outline and the polygons work on any list of cells. */
export interface HexCell {
  q: number;
  r: number;
  /** The hex's class (a coverage type, or a FreqMapper class). Same-class neighbours merge. */
  type: string;
}

/** The order of the values in a MeshMapper hex row (the API's `fields`). */
export const DEFAULT_HEX_FIELDS = ["q", "r", "type"];

/** Rows of [q, r, class] from the API into cells. Rows without a position or a class are dropped. */
export function cellsFromHexRows(rows: readonly (readonly unknown[])[], fields?: readonly string[] | null): HexCell[] {
  const list = fields && fields.length ? fields : DEFAULT_HEX_FIELDS;
  const qi = list.indexOf("q");
  const ri = list.indexOf("r");
  const ti = list.indexOf("type");
  const out: HexCell[] = [];
  for (const row of rows) {
    const q = row[qi];
    const r = row[ri];
    const type = row[ti];
    if (typeof q === "number" && typeof r === "number" && typeof type === "string") out.push({ q, r, type });
  }
  return out;
}

/** One number for a hex; q and r stay far inside +-2^21 for any real map. */
export function hexKey(q: number, r: number): number {
  return q * 4194304 + r;
}

// --- merged areas: the outline along the outer edge only ---------------------------------------------------------

export interface HexEdge {
  /** The hex that draws this edge. */
  q: number;
  r: number;
  /** Which edge of that hex (0 to 5, see NEIGHBOUR_STEP). */
  k: number;
  /** Coverage type of the hex that draws it. */
  type: string;
}

/** The edges to draw as outline. A hex edge is drawn when the hex across it is missing (the edge of the layer) or has
 * another coverage type (the edge between two merged areas). An edge shared by two hexes of the same type is never drawn,
 * so a run of same-type neighbours reads as one area. Where two types meet the edge is drawn once, by the hex whose type
 * sorts first. This is shared-edge cancellation on the grid: no geometry library. */
export function boundaryEdges(bins: readonly HexCell[]): HexEdge[] {
  const typeAt = new Map<number, string>();
  for (const b of bins) typeAt.set(hexKey(b.q, b.r), b.type);
  const out: HexEdge[] = [];
  for (const b of bins) {
    for (let k = 0; k < 6; k += 1) {
      const [dq, dr] = NEIGHBOUR_STEP[k];
      const other = typeAt.get(hexKey(b.q + dq, b.r + dr));
      if (other === undefined || (other !== b.type && b.type < other)) out.push({ q: b.q, r: b.r, k, type: b.type });
    }
  }
  return out;
}

// --- the fill paint -------------------------------------------------------------------------------------------------

/** Paint for a coverage layer's fill. `fill-antialias` is off on purpose: with it on (the map's default) MapLibre draws a
 * 1 px outline in the fill colour around every polygon, and since the fill is translucent each shared edge is drawn over
 * the fill twice and shows as a darker line inside a merged area. Merged areas must have no line inside; the only outline
 * is the one on the area's outer edge (the line layer, zoom 11 and up). */
export function hexFillPaint(colour: unknown, opacity: number): { "fill-color": unknown; "fill-opacity": number; "fill-antialias": false } {
  return { "fill-color": colour, "fill-opacity": opacity, "fill-antialias": false };
}

// --- GeoJSON -------------------------------------------------------------------------------------------------------

export interface HexFeature<P = { type: string }> {
  type: "Feature";
  geometry: { type: "Polygon"; coordinates: [number, number][][] };
  properties: P;
}

export interface EdgeFeature {
  type: "Feature";
  geometry: { type: "MultiLineString"; coordinates: [number, number][][] };
  properties: { type: string };
}

/** [west, south, east, north] */
export type Bounds = readonly [number, number, number, number];

function inView(q: number, r: number, sizeM: number, refLat: number, bounds: Bounds | null | undefined): boolean {
  if (!bounds) return true;
  const midLat = (bounds[1] + bounds[3]) / 2;
  const padLat = (sizeM / 111320) * 1.2;
  const padLon = padLat / Math.max(0.01, Math.cos((midLat * Math.PI) / 180));
  const [lat, lon] = hexCentre(q, r, sizeM, refLat);
  return !(lat < bounds[1] - padLat || lat > bounds[3] + padLat || lon < bounds[0] - padLon || lon > bounds[2] + padLon);
}

/** Hex polygons for the cells whose centre is inside `bounds` (padded by one hex), or all of them when no bounds. A polygon
 * carries only its class (`type`), which the map colours by. */
export function hexFeatures(cells: readonly HexCell[], sizeM: number, refLat: number, bounds?: Bounds | null): HexFeature[] {
  const out: HexFeature[] = [];
  for (const c of cells) {
    if (!inView(c.q, c.r, sizeM, refLat, bounds)) continue;
    out.push({ type: "Feature", geometry: { type: "Polygon", coordinates: [hexRing(c.q, c.r, sizeM, refLat)] }, properties: { type: c.type } });
  }
  return out;
}

/** The outline as one MultiLineString per coverage type (one source, one line layer), for the edges whose hex is in view. */
export function edgeFeatures(edges: readonly HexEdge[], sizeM: number, refLat: number, bounds?: Bounds | null): EdgeFeature[] {
  const perType = new Map<string, [number, number][][]>();
  for (const e of edges) {
    if (!inView(e.q, e.r, sizeM, refLat, bounds)) continue;
    let lines = perType.get(e.type);
    if (!lines) perType.set(e.type, (lines = []));
    lines.push([hexCorner(e.q, e.r, e.k, sizeM, refLat), hexCorner(e.q, e.r, (e.k + 1) % 6, sizeM, refLat)]);
  }
  const out: EdgeFeature[] = [];
  for (const [type, lines] of perType) out.push({ type: "Feature", geometry: { type: "MultiLineString", coordinates: lines }, properties: { type } });
  return out;
}
